import { type FC, useState, useEffect } from 'react'
import HeroCarousel from './components/HeroCarousel'
import AboutUsEducative from './components/AboutUsEducative'
import Features from './components/Features'
import InfoNews from './sections/InfoNews'
import ExpandablePaymentInstructions from './components/ExpandablePaymentInstructions'
import AttendantRegistryModal from '@/pages/auth/components/AttendantRegistryModal'
import { useAuth } from '@/hooks/useAuth'
import { UserRole } from '@/types/user.types'
import { Box, FormControlLabel, Switch, Fab, Tooltip } from '@mui/material'
import { PlayCircleOutline } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { fetchShowPaymentInstructions, updateShowPaymentInstructions } from '@/features/options/optionsThunks'

const Homepage: FC = () => {
  const { userRole, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const showPaymentInstructions = useAppSelector((state) => state.options.showPaymentInstructions)
  const canControlPaymentInstructions = userRole === UserRole.DIRECTIVE || userRole === UserRole.SUPERADMIN
  const [registryModalOpen, setRegistryModalOpen] = useState(false)
  const showFloatingButton = !isAuthenticated || (isAuthenticated && userRole === UserRole.ATTENDANT)

  // Fetch payment instructions visibility on mount
  useEffect(() => {
    dispatch(fetchShowPaymentInstructions())
  }, [dispatch])

  const handlePaymentClick = () => {
    if (!isAuthenticated) {
      // Si no está autenticado, abrir modal de registro
      setRegistryModalOpen(true)
    } else {
      // Si está autenticado, navegar a payments portal
      navigate('/payments/portal')
    }
  }

  const handleTutorialClick = () => {
    // URL del video tutorial - reemplazar con la URL real del video
    const tutorialUrl = 'https://youtu.be/u4Vzft5bewM'
    window.open(tutorialUrl, '_blank')
  }

  return (
    <main>
      {canControlPaymentInstructions && (
        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          px: { xs: 2, md: 4, lg: 6 },
          py: 1
        }}>
          <FormControlLabel
            control={
              <Switch
                checked={showPaymentInstructions}
                onChange={(e) => dispatch(updateShowPaymentInstructions(e.target.checked))}
                color="primary"
                size="small"
              />
            }
            label="Mostrar Instrucciones de Pago Bancario"
            labelPlacement="start"
            sx={{
              m: 0,
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                fontWeight: 500
              }
            }}
          />
        </Box>
      )}
      {showPaymentInstructions && <ExpandablePaymentInstructions />}
      <HeroCarousel />
      <InfoNews />
      <Features/>
      <AboutUsEducative />

      {/* Floating Action Button for Authenticated Users */}
      {showFloatingButton && (
        <>
          <Tooltip title="Pagar recursos institucionales" placement="left" arrow>
            <Fab
              color="secondary"
              aria-label="pagar artículos"
              onClick={handlePaymentClick}
              sx={{
                width: 84,
                height: 84,
                position: 'fixed',
                top: '50%',
                right: { xs: 16, md: 24 },
                transform: 'translateY(-50%)',
                zIndex: 1000,
                boxShadow: 3,
                backgroundColor: '#ffffff',
                '&:hover': {
                  transform: 'translateY(-50%) scale(1.1)',
                  transition: 'transform 0.2s ease-in-out',
                  backgroundColor: '#f5f5f5'
                }

              }}
            >
              <Box
                component="img"
                src="https://multimedia.epayco.co/epayco-landing/btns/epayco-logo-fondo-oscuro.png"
                alt="ePayco"
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  padding: 1
                }}
              />
            </Fab>
          </Tooltip>

          {/* Tutorial Video Button */}
          <Tooltip title="Tutorial de pagos en línea" placement="left" arrow>
            <Fab
              color="primary"
              aria-label="tutorial de pagos"
              onClick={handleTutorialClick}
              sx={{
                width: 84,
                height: 84,
                position: 'fixed',
                top: '50%',
                right: { xs: 16, md: 24 },
                transform: 'translateY(calc(-50% + 100px))',
                zIndex: 1000,
                boxShadow: 3,
                backgroundColor: '#1976d2',
                '&:hover': {
                  transform: 'translateY(calc(-50% + 100px)) scale(1.1)',
                  transition: 'transform 0.2s ease-in-out',
                  backgroundColor: '#1565c0'
                }
              }}
            >
              <PlayCircleOutline sx={{ fontSize: 40, color: '#fff' }} />
            </Fab>
          </Tooltip>
        </>
      )}

      {/* Attendant Registry Modal */}
      <AttendantRegistryModal
        open={registryModalOpen}
        onClose={() => setRegistryModalOpen(false)}
      />
    </main>
  )
}

export default Homepage
 